import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { addUser, getAllUserList, getOrganizersList, getUserById, getUsersList, userPointsAllocationLogs, createAmbassador, fetchCompanyDetails, fetchNgoDetails, getUserDeletionRequests, getUserDeletionRequestDetails, savePartnerAndSendCredentials, updateGreenAmbassador, sendAmbassadorCredentials, resolveUserDeletionRequest, getDeletedUsers, getDeletedUserDetails } from '../api/users';

export const useGetUsers = (toast) => {
    return useQuery({
        queryKey: ['users'],
        queryFn: () => getAllUserList(toast)
    });
}

export const useGetAllUsers = (toast) => {
    return useQuery({
        queryKey: ['allUsers'],
        queryFn: () => getUsersList(toast, "user"),
    });
}

export const useGetUserByUserType = ({ toast, userType = "user", filters = {} }) => {
    return useQuery({
        queryKey: ['allUsersByType', userType, filters],
        queryFn: () => getUsersList(toast, userType, { page: 1, limit: 50, ...filters }),
        // Keep data fresh but cache for 30 seconds to avoid repeated calls
        staleTime: 30000,
        cacheTime: 60000,
    });
}

export const useGetCompanies = () => {
    return useQuery({
        queryKey: ['companies'],
        queryFn: () => getUsersList(null, "company"),
    });
}

export const useGetNgos = () => {
    return useQuery({
        queryKey: ['ngos'],
        queryFn: () => getUsersList(null, "ngo"),
    });
}

export const useGetUserPointsAllocationLogs = (toast) => {
    return useQuery({
        queryKey: ['points-allocation-logs'],
        queryFn: () => userPointsAllocationLogs(toast)
    });
}

export const useGetActiveOrganiser = () => {
    return useQuery({
        queryKey: ['active-organiser'],
        queryFn: () => getOrganizersList()
    });
}

export const useCreateUser = (data) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationKey: ['create-user'],
        mutationFn: (data) => addUser(data),
        onSuccess: () => {
            queryClient.invalidateQueries(['users']);
            queryClient.invalidateQueries(['allUsers']);
            queryClient.invalidateQueries(['active-organiser']);
        },
    });
}

export const useGetUserById = (id) => {
    return useQuery({
        queryKey: ['user', id],
        queryFn: () => getUserById(id),
        enabled: !!id, // Only run this query if id is defined
    });
}

export const useCreateAmbassador = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationKey: ['create-ambassador'],
        mutationFn: ({ data, image, toast }) => createAmbassador(data, image, toast),
        onSuccess: () => {
            queryClient.invalidateQueries(['users']);
            queryClient.invalidateQueries(['allUsers']);
        },
    });
};

export const useUpdateAmbassador = (id) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['update-ambassador', id],
        mutationFn: (data) => updateGreenAmbassador(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['users']);
            queryClient.invalidateQueries(['allUsers']);
        },
    });
}

export const useGetCompanyInfo = (id) => {
    return useQuery({
        queryKey: ['company-info', id],
        queryFn: () => fetchCompanyDetails(id),
        enabled: !!id, // Only fetch when editing an existing company
        refetchOnWindowFocus: false, // Prevent refetch when window regains focus
        staleTime: 0, // Always consider data stale to fetch latest on edit
    });
}

export const useSavePartnerAndSendCredentials = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['save-partner'],
        mutationFn: (data) => savePartnerAndSendCredentials(data),
        onSuccess: () => {
            queryClient.invalidateQueries(['users']);
            queryClient.invalidateQueries(['allUsers']);
        },
    });
}

export const useSendAmbassadorCredentials = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['send-ambassador-credentials'],
        mutationFn: (data) => sendAmbassadorCredentials(data),
        onSuccess: () => {
            queryClient.invalidateQueries(['users']);
            queryClient.invalidateQueries(['allUsers']);
        },
    });
}

export const useGetNgoInfo = (id) => {
    return useQuery({
        queryKey: ['ngo-info', id],
        queryFn: () => fetchNgoDetails(id),
        enabled: !!id, // Only fetch when editing an existing NGO
        refetchOnWindowFocus: false, // Prevent refetch when window regains focus
        staleTime: 0, // Consider data fresh for 5 minutes
    });
}

// get user deletion requests
export const useGetUserDeletionRequests = () => {
    return useQuery({
        queryKey: ['user-deletion-requests'],
        queryFn: () => getUserDeletionRequests(),
    });
}

// get user deletion requests
export const useGetUserDeletionRequestDetails = (id) => {
    return useQuery({
        queryKey: ['user-deletion-requests', id],
        queryFn: () => getUserDeletionRequestDetails(id),
    });
}

// resolve user deletion request
export const useResolveDeletionRequest = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['resolve-deletion-request'],
        mutationFn: (data) => resolveUserDeletionRequest(data),
        onSuccess: () => {
            queryClient.invalidateQueries(['user-deletion-requests']);
        },
    });
}

// Get deleted users list
export const useGetDeletedUsers = () => {
    return useQuery({
        queryKey: ['deleted-users'],
        queryFn: () => getDeletedUsers(),
        staleTime: 0, // Always consider data stale
        refetchOnMount: true, // Always refetch on mount
        refetchOnWindowFocus: false, // Don't refetch on window focus
    });
}

// Get deleted user details
export const useGetDeletedUserDetails = (id) => {
    return useQuery({
        queryKey: ['deleted-users', id],
        queryFn: () => getDeletedUserDetails(id),
        enabled: !!id,
    });
}